import { useState, useEffect, useCallback } from "react";
import { Button, Textarea, TextInput, Select } from "../../common";
import { apiClient, apiService } from "../../../services/api";
import SynapseKindForm from "../synapse-kind/form/SynapseKindForm";
import SynapsePresetSelector from "./SynapsePresetSelector";

type SynapseKind = {
  id: string;
  value: string;
  label: string;
  kindName?: string;
  description?: string | null;
  hasNeuralProfile?: boolean;
};

type NodeResult = {
  id: string;
  nodeId?: string;
  name?: string;
  nodeName?: string;
  type?: string;
  nodeType?: string;
};

type SelectedNode = {
  id: string;
  name: string;
  type: string;
};

const PAGE_SIZE = 15;

const SynapseForm = ({
  edgeState,
  handleEdgeInputChange,
  handleSaveEdge,
  isAddMode = false,
  graphId,
}: {
  edgeState: any;
  handleEdgeInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleSaveEdge: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isAddMode?: boolean;
  graphId?: string;
}) => {
  const [synapseKinds, setSynapseKinds] = useState<SynapseKind[]>([]);
  const [showSynapseKindBuilder, setShowSynapseKindBuilder] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Node search state (add mode only)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState<"text" | "semantic">("text");
  const [assignTo, setAssignTo] = useState<"source" | "target">("source");
  const [searchResults, setSearchResults] = useState<NodeResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotalPages, setSearchTotalPages] = useState(0);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedSource, setSelectedSource] = useState<SelectedNode | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<SelectedNode | null>(null);

  const createChangeEvent = (name: string, value: string): React.ChangeEvent<HTMLSelectElement> => {
    return {
      target: { name, value },
    } as React.ChangeEvent<HTMLSelectElement>;
  };

  const fetchSynapseKinds = async () => {
    try {
      const kindsResponse = await apiClient.get("/edges/options/edge-kinds");
      if (kindsResponse.data.success) {
        setSynapseKinds(kindsResponse.data.kinds || []);
      }
    } catch (error) {
      console.error("Failed to load synapse kinds:", error);
    }
  };

  useEffect(() => {
    fetchSynapseKinds();
  }, []);

  // Annotate kinds without NeuralProfile in the dropdown label
  const synapseKindOptions = synapseKinds.map((k) => ({
    ...k,
    label: k.hasNeuralProfile === false ? `${k.label} (no profile)` : k.label,
  }));

  // Auto-populate weight/delay from SynapseKind neural profile defaults
  useEffect(() => {
    const kindId = edgeState.form?.synapseKindId;
    if (!kindId) return;

    const fetchDefaults = async () => {
      try {
        const resp = await apiClient.get(`/edges/options/edge-kinds/${kindId}/defaults`);
        if (resp.data.success && resp.data.defaults) {
          const d = resp.data.defaults;
          const midWeight =
            d.baseWeightMin != null && d.baseWeightMax != null
              ? ((d.baseWeightMin + d.baseWeightMax) / 2).toFixed(2)
              : null;
          const midDelay =
            d.baseDelayMinMs != null && d.baseDelayMaxMs != null
              ? Math.round((d.baseDelayMinMs + d.baseDelayMaxMs) / 2).toString()
              : null;

          if (midWeight != null && !edgeState.form.weight) {
            handleEdgeInputChange(createChangeEvent("weight", midWeight));
          }
          if (midDelay != null && !edgeState.form.delayMs) {
            handleEdgeInputChange(createChangeEvent("delayMs", midDelay));
          }
        }
      } catch {
        // defaults are best-effort
      }
    };

    fetchDefaults();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeState.form?.synapseKindId]);

  const executeSearch = useCallback(async (page: number) => {
    if (!graphId) return;

    setSearchLoading(true);
    setHasSearched(true);
    try {
      let response: any;
      const trimmed = searchQuery.trim();

      if (searchMode === "semantic") {
        response = await apiService.searchNodes({
          graphId,
          q: trimmed,
          mode: "semantic",
          page,
          pageSize: PAGE_SIZE,
        });
      } else {
        response = await apiService.listNodes({
          graphId,
          query: trimmed || undefined,
          page,
          pageSize: PAGE_SIZE,
        });
      }

      if (response.success) {
        setSearchResults(response.nodes || []);
        const total = response.totalCount || response.total || 0;
        setSearchTotalCount(total);
        setSearchTotalPages(Math.ceil(total / PAGE_SIZE));
        setSearchPage(page);
      } else {
        setSearchResults([]);
        setSearchTotalPages(0);
        setSearchTotalCount(0);
      }
    } catch {
      setSearchResults([]);
      setSearchTotalPages(0);
      setSearchTotalCount(0);
    } finally {
      setSearchLoading(false);
    }
  }, [graphId, searchQuery, searchMode]);

  const handleSearch = () => {
    executeSearch(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSearchTotalPages(0);
    setSearchTotalCount(0);
    setSearchPage(1);
    setHasSearched(false);
  };

  const handleSelectNode = (node: NodeResult) => {
    const nodeId = node.id || node.nodeId || "";
    const nodeName = node.name || node.nodeName || nodeId;
    const nodeType = node.type || node.nodeType || "";
    const selected: SelectedNode = { id: nodeId, name: nodeName, type: nodeType };

    if (assignTo === "source") {
      setSelectedSource(selected);
      handleEdgeInputChange(createChangeEvent("sourceNodeId", nodeId));
    } else {
      setSelectedTarget(selected);
      handleEdgeInputChange(createChangeEvent("targetNodeId", nodeId));
    }
  };

  const handleClearSource = () => {
    setSelectedSource(null);
    handleEdgeInputChange(createChangeEvent("sourceNodeId", ""));
  };

  const handleClearTarget = () => {
    setSelectedTarget(null);
    handleEdgeInputChange(createChangeEvent("targetNodeId", ""));
  };

  return (
    <>
      {isAddMode && (
        <div className="synapse-node-search">
          {/* Selected node chips */}
          <div className="synapse-node-search__selections">
            <div className="synapse-node-search__chip-group">
              <label className="synapse-node-search__chip-label">Source Node <span className="synapse-node-search__required">*</span></label>
              {selectedSource ? (
                <div className="synapse-node-search__chip">
                  <span>{selectedSource.name} <small>({selectedSource.type || "Node"})</small></span>
                  <button type="button" onClick={handleClearSource}>&times;</button>
                </div>
              ) : (
                <div className="synapse-node-search__chip synapse-node-search__chip--empty">
                  Not selected
                </div>
              )}
            </div>
            <div className="synapse-node-search__chip-group">
              <label className="synapse-node-search__chip-label">Target Node <span className="synapse-node-search__required">*</span></label>
              {selectedTarget ? (
                <div className="synapse-node-search__chip">
                  <span>{selectedTarget.name} <small>({selectedTarget.type || "Node"})</small></span>
                  <button type="button" onClick={handleClearTarget}>&times;</button>
                </div>
              ) : (
                <div className="synapse-node-search__chip synapse-node-search__chip--empty">
                  Not selected
                </div>
              )}
            </div>
          </div>

          {/* Search bar */}
          <div className="synapse-node-search__bar">
            <input
              type="text"
              className="synapse-node-search__input"
              placeholder="Search nodes by name... (press Enter or click Search)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <button
              type="button"
              className={`synapse-node-search__btn-search ${searchLoading ? "synapse-node-search__btn-search--loading" : ""}`}
              onClick={handleSearch}
              disabled={searchLoading}
            >
              {searchLoading ? "Searching..." : "Search"}
            </button>
            {hasSearched && (
              <button
                type="button"
                className="synapse-node-search__btn-clear"
                onClick={handleClearSearch}
              >
                Clear
              </button>
            )}
            <div className="synapse-node-search__mode-toggle">
              <button
                type="button"
                className={`synapse-node-search__mode-btn ${searchMode === "text" ? "active" : ""}`}
                onClick={() => setSearchMode("text")}
              >
                Text
              </button>
              <button
                type="button"
                className={`synapse-node-search__mode-btn ${searchMode === "semantic" ? "active" : ""}`}
                onClick={() => setSearchMode("semantic")}
              >
                Semantic
              </button>
            </div>
          </div>

          {/* Assign-to radio */}
          <div className="synapse-node-search__assign">
            <span className="synapse-node-search__assign-label">Assign selected result to:</span>
            <label className="synapse-node-search__radio">
              <input
                type="radio"
                name="assignTo"
                value="source"
                checked={assignTo === "source"}
                onChange={() => setAssignTo("source")}
              />
              <span>Source Node</span>
            </label>
            <label className="synapse-node-search__radio">
              <input
                type="radio"
                name="assignTo"
                value="target"
                checked={assignTo === "target"}
                onChange={() => setAssignTo("target")}
              />
              <span>Target Node</span>
            </label>
          </div>

          {/* Search results */}
          {hasSearched && (
            <div className="synapse-node-search__results">
              {searchLoading && (
                <div className="synapse-node-search__loading">Searching nodes...</div>
              )}
              {!searchLoading && searchResults.length === 0 && (
                <div className="synapse-node-search__empty">No nodes found</div>
              )}
              {!searchLoading && searchResults.length > 0 && (
                <>
                  <table className="synapse-node-search__table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((node) => {
                        const nodeId = node.id || node.nodeId || "";
                        const nodeName = node.name || node.nodeName || nodeId;
                        const nodeType = node.type || node.nodeType || "";
                        const isSelectedAsSource = selectedSource?.id === nodeId;
                        const isSelectedAsTarget = selectedTarget?.id === nodeId;
                        return (
                          <tr
                            key={nodeId}
                            className={`synapse-node-search__row ${isSelectedAsSource || isSelectedAsTarget ? "synapse-node-search__row--selected" : ""}`}
                            onClick={() => handleSelectNode(node)}
                          >
                            <td>{nodeName}</td>
                            <td>{nodeType}</td>
                            <td className="synapse-node-search__row-badge">
                              {isSelectedAsSource && <span className="synapse-node-search__badge synapse-node-search__badge--source">Source</span>}
                              {isSelectedAsTarget && <span className="synapse-node-search__badge synapse-node-search__badge--target">Target</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {searchTotalCount > 0 && (
                    <div className="synapse-node-search__footer">
                      <span className="synapse-node-search__count">{searchTotalCount} total results</span>
                      {searchTotalPages > 1 && (
                        <div className="synapse-node-search__pagination">
                          <button
                            type="button"
                            disabled={searchPage <= 1}
                            onClick={() => executeSearch(searchPage - 1)}
                          >
                            Prev
                          </button>
                          <span>Page {searchPage} of {searchTotalPages}</span>
                          <button
                            type="button"
                            disabled={searchPage >= searchTotalPages}
                            onClick={() => executeSearch(searchPage + 1)}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="synapse-entity-modal__form-group">
        <TextInput
          id="edge-name"
          name="edgeName"
          type="text"
          value={edgeState.form.edgeName}
          disabled={!isAddMode}
          onChange={handleEdgeInputChange}
          placeholder={isAddMode ? "Enter edge name" : ""}
          variant="modal"
          label="Edge"
          hint={isAddMode ? "Enter a name for this connection" : "Edge type is derived from the connection kind."}
        />
      </div>
      <div className="synapse-entity-modal__form-group">
        <Textarea
          id="edge-description"
          name="description"
          rows={4}
          value={edgeState.form.description}
          onChange={handleEdgeInputChange}
          placeholder="What does this connection represent?"
          variant="modal"
          label="Description"
        />
      </div>

      {/* Synapse Kind: always visible (top-level) */}
      <div className="synapse-entity-modal__form-group">
        <div className="synapse-kind-select-row">
          <div className="synapse-kind-select-row__select">
            <Select
              id="synapse-kind-id"
              name="synapseKindId"
              value={edgeState.form.synapseKindId || ""}
              onChange={handleEdgeInputChange}
              variant="modal"
              options={synapseKindOptions}
              placeholder="Select a connection kind"
              label="Connection Kind"
            />
          </div>
          <Button
            type="button"
            onClick={() => setShowSynapseKindBuilder(true)}
          >
            + Create Connection Kind
          </Button>
        </div>
      </div>
      {showSynapseKindBuilder && (
        <SynapseKindForm
          synapseKinds={synapseKinds}
          onClose={() => setShowSynapseKindBuilder(false)}
          onSuccess={(synapseKind: any) => {
            if (synapseKind) {
              const synapseKindId = synapseKind.synapsekindid || synapseKind.SynapseKindId || synapseKind.id;
              const kindName = synapseKind.kindname || synapseKind.KindName || synapseKind.kindName || '';
              const formattedSynapseKind: SynapseKind = {
                id: String(synapseKindId),
                value: String(synapseKindId),
                label: kindName || String(synapseKindId),
                kindName: kindName,
                description: synapseKind.description || synapseKind.Description || null,
                hasNeuralProfile: false,
              };
              setSynapseKinds([...synapseKinds, formattedSynapseKind]);
              if (synapseKindId) {
                handleEdgeInputChange(createChangeEvent("synapseKindId", String(synapseKindId)));
              }
            }
          }}
        />
      )}

      {/* Preset + Weight/Delay: always visible */}
      <SynapsePresetSelector
        edgeName={edgeState.form.edgeName || edgeState.form.relation || ""}
        currentWeight={edgeState.form.weight ? parseFloat(edgeState.form.weight) : undefined}
        onPresetApply={(preset) => {
          handleEdgeInputChange(createChangeEvent("weight", String(preset.weight)));
          handleEdgeInputChange(createChangeEvent("delayMs", String(preset.delayMs)));
        }}
      />
      <div className="synapse-entity-modal__fieldset">
        <div className="synapse-entity-modal__form-group">
          <TextInput
            id="edge-weight"
            name="weight"
            type="number"
            step="0.01"
            value={edgeState.form.weight}
            onChange={handleEdgeInputChange}
            placeholder="0.00"
            variant="modal"
            label="Weight"
          />
        </div>
        <div className="synapse-entity-modal__form-group">
          <TextInput
            id="edge-delay"
            name="delayMs"
            type="number"
            step="1"
            value={edgeState.form.delayMs}
            onChange={handleEdgeInputChange}
            placeholder="0"
            variant="modal"
            label="Delay (ms)"
          />
        </div>
      </div>

      {/* Advanced fields: collapsible in both modes */}
      <button
        type="button"
        className="synapse-advanced-toggle"
        onClick={() => setShowAdvanced(!showAdvanced)}
      >
        {showAdvanced ? "Hide" : "Show"} Advanced Settings
        <span className={`synapse-advanced-toggle__arrow ${showAdvanced ? "open" : ""}`}>&#9662;</span>
      </button>

      {showAdvanced && (
        <>
          <div className="synapse-entity-modal__form-group">
            <Textarea
              id="edge-attributes"
              name="attributesJson"
              rows={6}
              value={edgeState.form.attributesJson}
              onChange={handleEdgeInputChange}
              placeholder='{"attributes": ["tag1", "tag2"]}'
              variant="modal"
              label="Attributes (JSON)"
              hint="Attributes as JSON. Can be an array or object with nested data."
            />
          </div>
          <div className="synapse-entity-modal__form-group">
            <Textarea
              id="edge-synapseSettings"
              name="synapseSettingsJson"
              rows={6}
              value={edgeState.form.synapseSettingsJson}
              onChange={handleEdgeInputChange}
              placeholder='{"key": "value", "nested": {"property": "data"}}'
              variant="modal"
              label="Connection Settings (JSON)"
              hint="Connection settings as JSON object."
            />
          </div>
          <div className="synapse-entity-modal__form-group">
            <Textarea
              id="edge-resolvedSettings"
              name="resolvedSettingsJson"
              rows={6}
              value={edgeState.effectiveSettings?.resolvedSettingsJson || ""}
              onChange={(event) => {
                if (edgeState.handleEffectiveSettingsChange) {
                  edgeState.handleEffectiveSettingsChange(
                    "resolvedSettingsJson",
                    event.target.value
                  );
                }
              }}
              placeholder='{"setting1": "value1", "setting2": "value2"}'
              variant="modal"
              label="Resolved Settings (JSON)"
              hint="Effective resolved settings as JSON."
            />
          </div>
          <div className="synapse-entity-modal__form-group">
            <TextInput
              id="edge-effectiveSettingsSource"
              name="source"
              type="text"
              value={edgeState.effectiveSettings?.source || ""}
              onChange={(event) => {
                if (edgeState.handleEffectiveSettingsChange) {
                  edgeState.handleEffectiveSettingsChange(
                    "source",
                    event.target.value
                  );
                }
              }}
              placeholder="Source of effective settings"
              variant="modal"
              label="Source"
            />
          </div>
        </>
      )}

      {edgeState.error && (
        <p className="synapse-entity-modal__error">{edgeState.error}</p>
      )}
      {edgeState.success && (
        <p className="synapse-entity-modal__success">{edgeState.success}</p>
      )}
      <div className="synapse-entity-modal__footer">
        <Button
          type="button"
          variant="primary"
          onClick={handleSaveEdge}
          disabled={edgeState.saving}
        >
          {edgeState.saving ? (isAddMode ? "Creating…" : "Saving…") : (isAddMode ? "Create connection" : "Save changes")}
        </Button>
      </div>
    </>
  );
};

export default SynapseForm;
